import type { MeetingDetail, ParticipationBlock } from '@fruuit/shared'
import {
  Badge,
  Button,
  buttonClasses,
  Callout,
  Card,
  EmptyState,
  Eyebrow,
  LoadingRegion,
  PageHeader,
  StatusBadge,
  useToast,
} from '@fruuit/ui'
import { CalendarX2, MessageSquareText } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Link, useParams } from 'react-router'
import { errorMessage, isApiError } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { useTitle } from '../../lib/useTitle'
import { dateTimeIn, dateTimeUtc, rangeIn, rangeUtc, shortDate } from '../calendar/time'
import { MinutesSection } from '../minutes/MinutesSection'
import { type CalendarArea, meetingCommands, meetingKeys, useMeeting } from './api'
import { MeetingFormDialog } from './MeetingFormDialog'
import { ReasonDialog } from './ReasonDialog'
import { RescheduleDialog } from './RescheduleDialog'

export const MEETING_STATE_LABELS: Record<MeetingDetail['state'], string> = {
  draft: 'Team draft',
  published: 'Agenda published',
  cancelled: 'Cancelled',
  superseded: 'Rescheduled',
}

const PARTICIPATION_COPY: Record<ParticipationBlock, string> = {
  case_closed: 'This engagement is closed, so new participation requests are not open.',
  meeting_started: 'This meeting has started, so participation requests are closed.',
  cutoff_passed:
    'The participation deadline has passed. Send a question to the manager if you still want to contribute.',
  already_requested: 'You already have a participation request for this meeting.',
}

function stageLabel(stage: string): string {
  return stage.replace(/^S\d\s/, '')
}

function stageTone(stage: string): 'positive' | 'info' | 'quiet' {
  const n = Number(stage.charAt(1))
  return n === 4 ? 'positive' : n >= 2 ? 'info' : 'quiet'
}

function Detail({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-sage">{term}</dt>
      <dd className="mt-1 text-[15px] text-ink">{children}</dd>
    </div>
  )
}

type Action = 'edit' | 'publish' | 'cancel' | 'reschedule' | null

/**
 * S7 Meeting (CAL-037 + the demo journey): the brief, what to prepare
 * (objectives, investor questions, last correspondence), reviewed minutes with
 * history, commitments recorded and the follow-up. Investors see the released
 * view and their participation options; the case team gets the workflow.
 */
export function MeetingPage({ area }: { area: CalendarArea }) {
  const { id = '' } = useParams()
  const query = useMeeting(area, id)
  const meeting = query.data
  useTitle(meeting?.title ?? 'Meeting', 'Meeting')
  const base = `/${area}`
  const crumbs = [
    { label: 'Calendar', href: `${base}/calendar` },
    { label: meeting?.title ?? 'Meeting' },
  ]

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="Meeting" breadcrumbs={crumbs} />
        <LoadingRegion label="Loading the meeting…" />
      </>
    )
  }
  if (!meeting) {
    return (
      <>
        <PageHeader title="Meeting" breadcrumbs={crumbs} />
        <EmptyState
          icon={<CalendarX2 />}
          title="This meeting is not available."
          description={
            isApiError(query.error, 'not_found')
              ? 'It may have been cancelled or rescheduled, or it is not part of your engagements.'
              : errorMessage(query.error)
          }
          action={
            <Link to={`${base}/calendar`} className={buttonClasses({ variant: 'secondary' })}>
              Back to the calendar
            </Link>
          }
        />
      </>
    )
  }
  return <MeetingView meeting={meeting} area={area} crumbs={crumbs} />
}

function MeetingView({
  meeting: m,
  area,
  crumbs,
}: {
  meeting: MeetingDetail
  area: CalendarArea
  crumbs: { label: string; href?: string }[]
}) {
  const staff = area === 'workspace'
  const base = `/${area}`
  const { toast } = useToast()
  const [action, setAction] = useState<Action>(null)
  const invalidate = [meetingKeys.all]
  const update = useApiMutation(
    (body: Record<string, unknown>, ctx) => meetingCommands.update(m.id, body, ctx),
    { invalidate },
  )
  const publish = useApiMutation((_: null, ctx) => meetingCommands.publish(m.id, m.revision, ctx), {
    invalidate,
  })
  const cancel = useApiMutation(
    (reason: string, ctx) => meetingCommands.cancel(m.id, m.revision, reason, ctx),
    { invalidate },
  )
  const reschedule = useApiMutation(
    (body: Record<string, unknown>, ctx) => meetingCommands.reschedule(m.id, body, ctx),
    { invalidate },
  )
  const p = m.permissions
  const local = rangeIn(m.starts_at, m.ends_at, m.timezone)
  const utc = rangeUtc(m.starts_at, m.ends_at)
  const engagementHref = `${base}/engagements/${m.engagement.id}`

  const actions = staff ? (
    <>
      {p?.edit ? (
        <Button variant="secondary" onClick={() => setAction('edit')}>
          Edit draft
        </Button>
      ) : null}
      {p?.reschedule ? (
        <Button variant="secondary" onClick={() => setAction('reschedule')}>
          Reschedule
        </Button>
      ) : null}
      {p?.cancel ? (
        <Button variant="secondary" onClick={() => setAction('cancel')}>
          Cancel meeting
        </Button>
      ) : null}
      {p?.publish ? <Button onClick={() => setAction('publish')}>Publish agenda</Button> : null}
    </>
  ) : m.participation?.can_request ? (
    <Link
      to={`${engagementHref}?request=${m.id}`}
      className={buttonClasses({ variant: 'primary' })}
    >
      Request to participate
    </Link>
  ) : m.participation?.reason === 'already_requested' ? (
    <Link to="/app/requests" className={buttonClasses({ variant: 'secondary' })}>
      View participation request
    </Link>
  ) : null

  return (
    <>
      <PageHeader
        breadcrumbs={crumbs}
        eyebrow={`${m.company.name} · ${m.meeting_type}`}
        title={m.title}
        description={<span title={utc}>{local}</span>}
        actions={actions}
      />

      <div className="mb-6 flex flex-col gap-3">
        {m.state === 'cancelled' ? (
          <Callout tone="warning" title="This meeting was cancelled">
            {m.cancel_reason ?? 'No reason was recorded.'}
          </Callout>
        ) : null}
        {m.state === 'superseded' ? (
          <Callout tone="warning" title="This meeting was rescheduled">
            {m.reschedule_reason}{' '}
            {m.superseded_by ? (
              <Link
                className="font-semibold underline"
                to={`${base}/meetings/${m.superseded_by.id}`}
              >
                See the new time: {dateTimeIn(m.superseded_by.starts_at, m.timezone)}
              </Link>
            ) : null}
          </Callout>
        ) : null}
        {m.supersedes ? (
          <Callout tone="info" title="Rescheduled">
            Moved from {dateTimeIn(m.supersedes.starts_at, m.timezone)}
            {m.supersedes.reason ? `: ${m.supersedes.reason}` : '.'}
          </Callout>
        ) : null}
        {m.state === 'draft' ? (
          <Callout tone="note" title="Team draft">
            Only the case team can see this meeting. Publishing announces the agenda to subscribed
            investors; no invitations are sent.
          </Callout>
        ) : null}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-6">
          <Card
            title="Meeting brief"
            actions={<StatusBadge status={MEETING_STATE_LABELS[m.state]} />}
          >
            <dl className="grid gap-5 sm:grid-cols-2">
              <Detail term="Date and time">
                {local}
                {m.timezone !== 'UTC' ? (
                  <span className="block text-sm text-sage">{utc}</span>
                ) : null}
              </Detail>
              <Detail term="Format">{m.location}</Detail>
              <Detail term="Company participants">
                {m.company_participants || 'To be confirmed'}
              </Detail>
              <Detail term="Participation deadline">
                <span title={dateTimeUtc(m.participation_cutoff)}>
                  {dateTimeIn(m.participation_cutoff, m.timezone)}
                </span>
              </Detail>
            </dl>
            <h3 className="mt-6 text-sm font-semibold text-teal-800">Agenda</h3>
            <p className="mt-1 whitespace-pre-line text-[15px] leading-relaxed">{m.agenda}</p>
            {m.objectives.length ? (
              <>
                <h3 className="mt-6 text-sm font-semibold text-teal-800">Related objectives</h3>
                <ul className="mt-1 flex flex-col gap-1">
                  {m.objectives.map((o) => (
                    <li key={o.id}>
                      <Link
                        className="text-[15px] font-semibold text-teal underline-offset-2 hover:underline"
                        to={`${engagementHref}#objective-${o.id}`}
                      >
                        {o.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </Card>

          <Preparation meeting={m} engagementHref={engagementHref} />

          <MinutesSection meeting={m} staff={staff} />

          <Card title="Commitments recorded" eyebrow="After this meeting">
            {m.commitments.length === 0 ? (
              <p className="text-sm text-sage">
                No commitment at stage S2 or later has been recorded on the related objectives since
                this meeting.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-line-soft">
                {m.commitments.map((c) => (
                  <li key={c.milestone_id} className="py-3 first:pt-0 last:pb-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={stageTone(c.stage)}>{stageLabel(c.stage)}</Badge>
                      {c.state !== 'published' ? <StatusBadge status={c.state} /> : null}
                      <span className="text-xs text-sage">{shortDate(c.assessment_date)}</span>
                    </div>
                    <p className="mt-1 font-semibold">{c.objective_title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-ink-soft">{c.rationale}</p>
                    <p className="mt-1 text-xs text-sage">Limits: {c.limitations}</p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Follow-up" eyebrow="Next steps per objective">
            {m.follow_up.length === 0 ? (
              <p className="text-sm text-sage">No objectives are linked to this engagement yet.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-line-soft">
                {m.follow_up.map((f) => (
                  <li
                    key={f.objective_id}
                    className="grid gap-1 py-3 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-4"
                  >
                    <div className="min-w-0">
                      <p className="font-semibold">{f.title}</p>
                      <p className="text-sm text-ink-soft">{f.next_action}</p>
                    </div>
                    <p className="text-sm text-sage sm:text-right">
                      Due {shortDate(f.due_date)}
                      {f.owner ? <span className="block">{f.owner}</span> : null}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <aside className="flex flex-col gap-6">
          <Card variant="tint" padding="md">
            <Eyebrow>Meeting owner</Eyebrow>
            <p className="mt-2 font-display text-lg font-semibold">
              {m.manager?.name ?? 'Case manager'}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-ink-soft">
              {staff
                ? `Reviewer: ${m.reviewer?.name ?? '—'}. Minutes are released only after independent review.`
                : 'Prepare your question or ask to take part. The manager’s acknowledgment is a planning step, not a booking.'}
            </p>
            {!staff && m.participation && !m.participation.can_request && m.participation.reason ? (
              <p className="mt-3 rounded-md bg-surface px-3 py-2 text-sm text-ink-soft">
                {PARTICIPATION_COPY[m.participation.reason]}
              </p>
            ) : null}
            <div className="mt-4 flex flex-col gap-2">
              <Link
                to={engagementHref}
                className={buttonClasses({ variant: 'secondary', fullWidth: true })}
              >
                {staff ? 'Open the case' : 'Send a question to the manager'}
              </Link>
              <Link to={engagementHref} className="text-sm font-semibold text-teal hover:underline">
                Full engagement plan →
              </Link>
            </div>
          </Card>
          <Card variant="surface" padding="md">
            <h2 className="font-display text-base font-semibold">What happens next?</h2>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-ink-soft">
              <li>The manager reviews participation requests.</li>
              <li>Discussion and company responses are recorded.</li>
              <li>An independent reviewer releases the minutes.</li>
              <li>Objective progress changes only when evidence supports it.</li>
            </ol>
          </Card>
          <p className="text-xs leading-relaxed text-sage">
            Meetings are internal planning records. Fruuit Engage does not send calendar invitations
            or contact companies. Fictional demo data.
          </p>
        </aside>
      </div>

      {staff ? (
        <>
          <MeetingFormDialog
            open={action === 'edit'}
            onClose={() => setAction(null)}
            meeting={m}
            onSubmit={async (body) => {
              await update.mutateAsync(body)
              toast({ tone: 'success', message: 'Draft meeting saved.' })
            }}
          />
          <RescheduleDialog
            open={action === 'reschedule'}
            onClose={() => setAction(null)}
            meeting={m}
            onSubmit={async (body) => {
              const next = await reschedule.mutateAsync(body)
              toast({
                tone: 'success',
                message: `Rescheduled to ${dateTimeIn(next.starts_at, next.timezone)}.`,
              })
            }}
          />
          <ReasonDialog
            open={action === 'cancel'}
            onClose={() => setAction(null)}
            title="Cancel this meeting"
            description="Investors will no longer see it. The record and its reason stay in the audit trail."
            label="Why is it cancelled?"
            submitLabel="Cancel meeting"
            danger
            onSubmit={async (reason) => {
              await cancel.mutateAsync(reason)
              toast({ tone: 'success', message: 'Meeting cancelled.' })
            }}
          />
          <PublishDialog
            open={action === 'publish'}
            onClose={() => setAction(null)}
            onConfirm={async () => {
              await publish.mutateAsync(null)
              toast({ tone: 'success', message: 'Agenda published to subscribed investors.' })
            }}
          />
        </>
      ) : null}
    </>
  )
}

function Preparation({
  meeting: m,
  engagementHref,
}: {
  meeting: MeetingDetail
  engagementHref: string
}) {
  const prep = m.preparation
  return (
    <Card title="Preparation" eyebrow="Bring to the meeting">
      <h3 className="text-sm font-semibold text-teal-800">Objectives and the next tests</h3>
      {prep.objectives.length === 0 ? (
        <p className="mt-1 text-sm text-sage">
          No objectives are released for this engagement yet.
        </p>
      ) : (
        <ul className="mt-2 flex flex-col gap-3">
          {prep.objectives.map((o) => (
            <li key={o.id} className="rounded-lg border border-line p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="font-semibold">{o.title}</p>
                <Badge tone={stageTone(o.stage)}>{stageLabel(o.stage)}</Badge>
              </div>
              <p className="mt-1 text-sm text-ink-soft">
                <span className="font-semibold">Change sought:</span> {o.change_request}
              </p>
              {o.latest_evidence ? (
                <p className="mt-1 text-sm text-ink-soft">
                  <span className="font-semibold">On record:</span> {o.latest_evidence}
                </p>
              ) : null}
              <p className="mt-2 rounded-md bg-wash px-3 py-2 text-sm">
                <span className="font-semibold text-teal-800">Test at this meeting:</span>{' '}
                {o.next_action}
                <span className="block text-xs text-sage">
                  Due {shortDate(o.due_date)}
                  {o.owner ? ` · ${o.owner}` : ''}
                </span>
              </p>
            </li>
          ))}
        </ul>
      )}

      <h3 className="mt-6 text-sm font-semibold text-teal-800">
        Investor questions on this engagement
      </h3>
      {prep.questions.length === 0 ? (
        <p className="mt-1 text-sm text-sage">
          No questions yet.{' '}
          <Link to={engagementHref} className="font-semibold text-teal hover:underline">
            Ask the manager
          </Link>
        </p>
      ) : (
        <ul className="mt-2 flex flex-col divide-y divide-line-soft">
          {prep.questions.map((q) => (
            <li key={q.id} className="flex items-start gap-3 py-2">
              <MessageSquareText
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0 text-teal-400"
              />
              <div className="min-w-0 flex-1">
                <p className="text-[15px]">{q.subject}</p>
                <p className="text-xs text-sage">
                  {[q.asked_by, q.org].filter(Boolean).join(' · ')} · {shortDate(q.last_message_at)}
                </p>
              </div>
              <StatusBadge status={q.status} />
            </li>
          ))}
        </ul>
      )}

      <h3 className="mt-6 text-sm font-semibold text-teal-800">Last correspondence</h3>
      {prep.last_correspondence ? (
        <div className="mt-2 rounded-lg border border-line bg-cream/60 p-4">
          <p className="text-xs text-sage">{shortDate(prep.last_correspondence.event_date)}</p>
          <p className="font-semibold">{prep.last_correspondence.title}</p>
          <p className="mt-1 text-sm text-ink-soft">
            <span className="font-semibold">Company:</span>{' '}
            {prep.last_correspondence.company_response}
          </p>
          <p className="mt-1 text-sm text-ink-soft">
            <span className="font-semibold">Fruuit:</span>{' '}
            {prep.last_correspondence.fruuit_judgment}
          </p>
        </div>
      ) : (
        <p className="mt-1 text-sm text-sage">No correspondence before this meeting.</p>
      )}
    </Card>
  )
}

function PublishDialog({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
}) {
  return (
    <ReasonDialog
      open={open}
      onClose={onClose}
      title="Publish the agenda"
      description="Subscribed investors will see the meeting, its agenda and the participation deadline. After publishing, the schedule can only change by rescheduling."
      label="Note for the audit trail"
      optional
      submitLabel="Publish agenda"
      onSubmit={() => onConfirm()}
    />
  )
}
